/**
 * Product Image Gallery Component
 * Responsive image gallery specifically designed for QR verification display
 */

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { ProductImageDisplay } from '../services/qr.service';
import Modal from './ui/Modal';
import Button from './ui/Button';

interface ProductImageGalleryProps {
  images: ProductImageDisplay[];
  productName: string;
  className?: string;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  images,
  productName,
  className = ''
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<string>>(new Set());

  const toImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const base = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
    const normalized = url.replace(/^\/+/, '');
    return base ? `${base}/${normalized}` : `/${normalized}`;
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedImageIndex(null);
  };

  const handlePrevious = () => {
    if (selectedImageIndex !== null && selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1);
    }
  };

  const handleNext = () => {
    if (selectedImageIndex !== null && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1);
    }
  };

  const handleImageError = (imageId: string) => {
    setImageLoadErrors(prev => new Set(prev).add(imageId));
  };

  const getImageUrl = (image: ProductImageDisplay, useThumbnail: boolean = false): string => {
    if (imageLoadErrors.has(image.id)) {
      return ''; // Return empty string for failed images
    }
    const primary = toImageUrl(image.url) || '';
    const thumb = toImageUrl(image.thumbnailUrl) || '';
    const fallback = useThumbnail ? thumb || primary : primary || thumb;
    return fallback;
  };

  if (!images || images.length === 0) {
    return null;
  }

  // Sort images by display order, with primary image first
  const sortedImages = [...images].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return a.displayOrder - b.displayOrder;
  });

  const selectedImage = selectedImageIndex !== null ? sortedImages[selectedImageIndex] : null;

  return (
    <>
      <div className={`space-y-4 ${className}`}>
        {/* Main Image Display */}
        {sortedImages.length > 0 && (
          <div className="relative">
            <div 
              className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
              onClick={() => handleImageClick(0)}
            >
              {!imageLoadErrors.has(sortedImages[0].id) ? (
                <>
                  <img
                    src={getImageUrl(sortedImages[0])}
                    alt={sortedImages[0].altText || `${productName} - Main Image`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    onError={() => handleImageError(sortedImages[0].id)}
                    loading="lazy"
                  />
                  {/* Zoom Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <ZoomIn className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-200">
                  <div className="text-center text-gray-500">
                    <div className="w-16 h-16 mx-auto mb-2 bg-gray-300 rounded-lg flex items-center justify-center">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-sm">Image unavailable</p>
                  </div>
                </div>
              )}
            </div>

            {/* Image Counter Badge */}
            {sortedImages.length > 1 && (
              <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white text-sm px-2 py-1 rounded">
                1 of {sortedImages.length}
              </div>
            )}
          </div>
        )}

        {/* Thumbnail Strip */}
        {sortedImages.length > 1 && (
          <div className="grid grid-cols-4 gap-2">
            {sortedImages.slice(1, 5).map((image, index) => (
              <div
                key={image.id}
                className="aspect-square bg-gray-100 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all"
                onClick={() => handleImageClick(index + 1)}
              >
                {!imageLoadErrors.has(image.id) ? (
                  <img
                    src={getImageUrl(image, true)}
                    alt={image.altText || `${productName} - Image ${index + 2}`}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(image.id)}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>
            ))}

            {/* More Images Indicator */}
            {sortedImages.length > 5 && (
              <div
                className="aspect-square bg-gray-800 bg-opacity-80 rounded-lg flex items-center justify-center cursor-pointer hover:bg-opacity-90 transition-all"
                onClick={() => handleImageClick(4)}
              >
                <div className="text-center text-white">
                  <span className="text-lg font-bold">+{sortedImages.length - 4}</span>
                  <p className="text-xs">more</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* View All Images Button */}
        {sortedImages.length > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleImageClick(0)}
            className="w-full"
          >
            <ZoomIn className="w-4 h-4 mr-2" />
            View All Images ({sortedImages.length})
          </Button>
        )}
      </div>

      {/* Fullscreen Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        size="xl"
        showCloseButton={false}
      >
        {selectedImage && (
          <div className="relative">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {productName} - Images
                </h3>
                <p className="text-sm text-gray-500">
                  {(selectedImageIndex || 0) + 1} of {sortedImages.length}
                </p>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseModal}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Image Display */}
            <div className="relative bg-gray-900 flex items-center justify-center min-h-[60vh] max-h-[80vh]">
              {!imageLoadErrors.has(selectedImage.id) ? (
                <img
                  src={getImageUrl(selectedImage)}
                  alt={selectedImage.altText || `${productName} - Image ${(selectedImageIndex || 0) + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <div className="text-center text-white">
                  <div className="w-24 h-24 mx-auto mb-4 bg-gray-700 rounded-lg flex items-center justify-center">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p>Image could not be loaded</p>
                </div>
              )}

              {/* Navigation Buttons */}
              {sortedImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrevious}
                    disabled={selectedImageIndex === 0}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  
                  <button
                    onClick={handleNext}
                    disabled={selectedImageIndex === sortedImages.length - 1}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail Navigation */}
            {sortedImages.length > 1 && (
              <div className="p-4 border-t bg-gray-50">
                <div className="flex gap-2 overflow-x-auto">
                  {sortedImages.map((image, index) => (
                    <button
                      key={image.id}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index
                          ? 'border-blue-500 ring-2 ring-blue-200'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {!imageLoadErrors.has(image.id) ? (
                        <img
                          src={getImageUrl(image, true)}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={() => handleImageError(image.id)}
                        />
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default ProductImageGallery;
